import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { FaCalendarCheck, FaWhatsapp } from "react-icons/fa";
import { GrFormView } from "react-icons/gr";
import { HiOutlinePhoneMissedCall } from "react-icons/hi";
import { SlCallOut } from "react-icons/sl";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../redux/authSlice";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Webcam from "react-webcam";
import LoaderSpiner from "../Components/LoaderSpiner";
import axios from "axios";
import styles from "./Styles/CreateElectionOutBoundAgentComponent.module.css";

const languages = [
  { value: "en-US", name: "English (United States)" },
  { value: "en-IN", name: "English (India)" },
  { value: "en-GB", name: "English (United Kingdom)" },
  { value: "en-AU", name: "English (Australia)" },
  { value: "en-NZ", name: "English (New Zealand)" },
  { value: "hi", name: "Hindi" },
  { value: "ar", name: "Arabic" },
  { value: "es", name: "Spanish" },
  { value: "pl", name: "Polish" },
  { value: "pt", name: "Portuguese" },
  { value: "ta", name: "Tamil" },
  { value: "ro", name: "Romanian" },
  { value: "fi", name: "Finnish" },
  { value: "el", name: "Greek" },
  { value: "bg", name: "Bulgarian" },
  { value: "vi", name: "Vietnamese" },
  { value: "cs", name: "Czech" },
  { value: "no", name: "Norwegian" },
  { value: "fr", name: "French" },
  { value: "de", name: "German" },
  { value: "ja", name: "Japanese" },
  { value: "ru", name: "Russian" },
  { value: "it", name: "Italian" },
  { value: "sv", name: "Swedish" },
  { value: "nl", name: "Dutch" },
  { value: "uk", name: "Ukrainian" },
  { value: "tr", name: "Turkish" },
  { value: "zh", name: "Chinese" },
];

const templateImages = [
  "/avatar1.jpg",
  "/avatar2.jpg",
  "/avatar3.jpg",
  "/avatar4.jpg",
  "/avatar5.png",
  "/avatar6",
  "/avatar7.webp",
  "/avatar8.webp",
  "/avatar9.webp",
];

const API_BASE_URL = "https://prod.toxboxai.com/api/election-agent";

const ElectionEditOutboundAgent = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { token, userId, isAuthenticated } = useSelector((state) => state.auth);

  // Agent data states
  const [avatarName, setAvatarName] = useState("");
  const [avatarPrefix, setAvatarPrefix] = useState("");
  const [avatarImage, setAvatarImage] = useState(null);
  const [language, setLanguage] = useState("");
  const [gender, setGender] = useState("");
  const [voice, setVoice] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [model, setModel] = useState("");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [temperature, setTemperature] = useState(0.3);
  const [firstSpeaking, setFirstSpeaking] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState("");
  const [maxDuration, setMaxDuration] = useState("");
  const [timeExceedMessage, setTimeExceedMessage] = useState("");
  const [transcript, setTranscript] = useState(false);
  const [callRecording, setCallRecording] = useState(false);
  const [corpusId, setCorpusId] = useState("");
  const [inactivityMessages, setInactivityMessages] = useState([
    { duration: "", message: "" },
  ]);

  // Tool states
  const [showCalcomPopup, setShowCalcomPopup] = useState(false);
  const [calcomToken, setCalcomToken] = useState("");
  const [showCallTransferPopup, setShowCallTransferPopup] = useState(false);
  const [callTransferNumber, setCallTransferNumber] = useState("");
  const [showWhatsappPopup, setShowWhatsappPopup] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [bookCalendar, setBookCalendar] = useState(false);
  const [whatsapp, setWhatsapp] = useState(false);
  const [callTransfer, setCallTransfer] = useState(false);

  // Supporting data states
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [isLoadingNumbers, setIsLoadingNumbers] = useState(false);
  const [numberError, setNumberError] = useState("");
  const [voices, setVoices] = useState([]);
  const [isLoadingVoices, setIsLoadingVoices] = useState(false);
  const [voiceError, setVoiceError] = useState("");
  const [prompts, setPrompts] = useState([]);
  const [isLoadingPrompts, setIsLoadingPrompts] = useState(false);
  const [promptError, setPromptError] = useState("");
  const [corpora, setCorpora] = useState([]);
  const [isLoadingCorpora, setIsLoadingCorpora] = useState(false);
  const [corpusError, setCorpusError] = useState("");
  const [voiceSearch, setVoiceSearch] = useState("");

  // UI states
  const [currentPage, setCurrentPage] = useState(1);
  const [showAvatarPanel, setShowAvatarPanel] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const webcamRef = useRef(null);
  const voicesPerPage = 5;

  // Authentication check
  useEffect(() => {
    if (!token || !userId || !isAuthenticated || !agentId) {
      dispatch(logout());
      navigate("/login");
    }
  }, [token, userId, isAuthenticated, agentId, dispatch, navigate]);

  // Infer language from displayName
  const getVoiceLanguage = (displayName) => {
    if (displayName.includes("En (Indian)")) return "en-IN";
    if (displayName.includes("En (British)")) return "en-GB";
    if (displayName.includes("En (Australian)")) return "en-AU";
    if (displayName.includes("En (NewZealand)")) return "en-NZ";
    if (displayName.includes("-En") || displayName.includes("En ")) return "en-US";
    if (displayName.includes("Hi (Urdu)") || displayName.includes("-Hi")) return "hi";
    if (displayName.includes("Ar (Egyptian)") || displayName.includes("-Ar")) return "ar";
    if (displayName.includes("-Sp")) return "es";
    if (displayName.includes("-Po")) return "pl";
    if (displayName.includes("Br (Portuguese)")) return "pt";
    if (displayName.includes("-Ta")) return "ta";
    if (displayName.includes("-Ro")) return "ro";
    if (displayName.includes("-Fi")) return "fi";
    if (displayName.includes("-Gr")) return "el";
    if (displayName.includes("-Bu")) return "bg";
    if (displayName.includes("-Vi")) return "vi";
    if (displayName.includes("-Cz")) return "cs";
    if (displayName.includes("-No")) return "no";
    if (displayName.includes("-Fr")) return "fr";
    if (displayName.includes("-Ge")) return "de";
    if (displayName.includes("-Ja")) return "ja";
    if (displayName.includes("-Ru")) return "ru";
    if (displayName.includes("-It")) return "it";
    if (displayName.includes("-Sw")) return "sv";
    if (displayName.includes("-Du")) return "nl";
    if (displayName.includes("-Uk")) return "uk";
    if (displayName.includes("-Tu")) return "tr";
    if (displayName.includes("-Ch")) return "zh";
    return null;
  };

  // Enhanced voice filtering
  const filteredVoices = useMemo(() => {
    return voices.filter((v) => {
      const voiceLanguage = getVoiceLanguage(v.displayName);
      const matchesGender =
        !gender ||
        v.gender?.toLowerCase() === gender.toLowerCase() ||
        v.gender === "Unknown" ||
        !v.gender;
      const matchesLanguage = !language || (voiceLanguage && voiceLanguage === language);
      const matchesSearch =
        !voiceSearch ||
        v.displayName.toLowerCase().includes(voiceSearch.toLowerCase());
      return matchesGender && matchesLanguage && matchesSearch;
    });
  }, [gender, language, voiceSearch, voices]);

  const indexOfLastVoice = currentPage * voicesPerPage;
  const indexOfFirstVoice = indexOfLastVoice - voicesPerPage;
  const currentVoices = filteredVoices.slice(indexOfFirstVoice, indexOfLastVoice);
  const totalPages = Math.ceil(filteredVoices.length / voicesPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Unified error handling
  const handleApiError = (err, defaultMessage) => {
    const message = err.response?.data?.error || defaultMessage;
    toast.error(message);
    if (err.response?.status === 401) {
      dispatch(logout());
      navigate("/login");
    }
    return message;
  };

  // Fetch agent data by ID
  const fetchAgentData = useCallback(
    async (promptsList = []) => {
      if (!token || !agentId) return;

      try {
        setIsLoading(true);
        setError(null);

        const response = await axios.get(`${API_BASE_URL}/by-id/${agentId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.status === 401) {
          dispatch(logout());
          navigate("/login");
          return;
        }

        if (response.status === 200) {
          const agent = response.data.agent || response.data;

          // Pre-populate all form fields
          if (agent.avatarName) {
            const prefixMatch = agent.avatarName.match(/^([A-Z]{3})-(.+)$/);
            if (prefixMatch) {
              setAvatarPrefix(prefixMatch[1]);
              setAvatarName(prefixMatch[2]);
            } else {
              setAvatarName(agent.avatarName);
            }
          }

          setAvatarImage(agent.avatarImage || null);
          setLanguage(agent.language || "");
          setGender(agent.gender || "");
          setVoice(
            agent.voice && agent.voice.displayName
              ? agent.voice.displayName
              : agent.voice || ""
          );
          setPhoneNumber(agent.phoneNumber || "");
          setModel(agent.model || "");

          // Handle systemPrompt
          const systemPromptId = agent.systemPrompt?.content?.promptId || "";
          if (systemPromptId && promptsList.length > 0) {
            const promptExists = promptsList.some(
              (p) => p._id === systemPromptId
            );
            if (promptExists) {
              setSystemPrompt(systemPromptId);
            } else {
              setPromptError("Selected system prompt is not available");
              setSystemPrompt("");
            }
          } else {
            setSystemPrompt(systemPromptId);
          }

          setTemperature(agent.temperature || 0.3);
          setFirstSpeaking(agent.firstSpeaking || false);
          setWelcomeMessage(
            agent.firstSpeakerSettings?.agent?.text ||
            agent.welcomeMessage ||
            ""
          );

          if (agent.maxDuration) {
            const durationMatch = agent.maxDuration.match(/(\d+)s/);
            if (durationMatch) {
              setMaxDuration(durationMatch[1]);
            }
          }

          setTimeExceedMessage(agent.timeExceedMessage || "");
          setTranscript(agent.transcript || false);
          setCallRecording(agent.callRecording || false);
          setBookCalendar(agent.bookCalendar || false);
          setWhatsapp(agent.whatsapp || false);
          setCallTransfer(agent.callTransfer || false);
          setWhatsappNumber(agent.whatsappNumber || "");
          setCallTransferNumber(agent.callTransferNumber || "");
          setCalcomToken(agent.calComApiKey || "");
          // Set inactivityMessages from API response
          setInactivityMessages(
            Array.isArray(agent.inactivityMessages) && agent.inactivityMessages.length > 0
              ? agent.inactivityMessages.map((msg) => ({
                duration: msg.duration.replace("s", ""),
                message: msg.message,
              }))
              : [{ duration: "", message: "" }]
          );
        }
      } catch (err) {
        const errorMessage = handleApiError(err, "Failed to load agent data");
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [agentId, token, dispatch, navigate]
  );

  // Fetch supporting data
  const fetchSupportingData = useCallback(
    async () => {
      try {
        // Fetch phone numbers
        setIsLoadingNumbers(true);
        setNumberError("");
        try {
          const numbersResponse = await axios.get(
            `https://prod.toxboxai.com/api/election-number/by-purchase-person/${userId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );
          if (numbersResponse.status === 200) {
            setPhoneNumbers(
              Array.isArray(numbersResponse.data.numbers)
                ? numbersResponse.data.numbers
                : []
            );
          }
        } catch (err) {
          setNumberError(handleApiError(err, "Failed to load phone numbers"));
        } finally {
          setIsLoadingNumbers(false);
        }

        // Fetch voices
        setIsLoadingVoices(true);
        setVoiceError("");
        try {
          const voicesResponse = await axios.get(
            "https://prod.toxboxai.com/api/voice/get-voice-by-display-name",
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );
          if (voicesResponse.status === 200) {
            setVoices(Array.isArray(voicesResponse.data) ? voicesResponse.data : []);
          } else {
            setVoiceError("Failed to load voices");
          }
        } catch (err) {
          setVoiceError(handleApiError(err, "Failed to load voices"));
        } finally {
          setIsLoadingVoices(false);
        }


        // Fetch prompts
        setIsLoadingPrompts(true);
        setPromptError("");
        try {
          const promptsResponse = await axios.get(
            `https://prod.toxboxai.com/api/prompt-template/get-templates-by-creator`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (promptsResponse.status === 200) {
            const promptsData = Array.isArray(promptsResponse.data.templates)
              ? promptsResponse.data.templates
              : [];
            setPrompts(promptsData);
            await fetchAgentData(promptsData);
          }
        } catch (err) {
          setPromptError(handleApiError(err, "Failed to load prompts"));
        } finally {
          setIsLoadingPrompts(false);
        }
      } catch (err) {
        handleApiError(err, "Failed to load supporting data");
      }
    },
    [token, userId, fetchAgentData]
  );

  useEffect(() => {
    if (token && userId) {
      fetchSupportingData();
    }
  }, [token, userId, fetchSupportingData]);

  const handleFileUpload = (e) => {
    if (e.target.files && e.target.files[0]) {
      setAvatarImage(e.target.files[0]);
      setShowAvatarPanel(false);
    }
  };

  const handleAvatarSelect = (imgUrl) => {
    setAvatarImage(imgUrl);
    setShowAvatarPanel(false);
  };

  const capturePhoto = () => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      setAvatarImage(imageSrc);
      setShowCamera(false);
    }
  };

  const handleVoiceSelect = (displayName) => {
    const selectedVoice = voices.find((v) => v.displayName === displayName);
    if (selectedVoice) {
      setVoice(displayName);
      setCurrentPage(1);
    } else {
      toast.error("Voice not found");
    }
  };

  const handleAvatarNameChange = (e) => {
    const name = e.target.value.replace(`${avatarPrefix}-`, "");
    if (!avatarPrefix && name) {
      setAvatarPrefix(generateRandomPrefix());
    }
    setAvatarName(name);
  };

  const generateRandomPrefix = () => {
    const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    let result = "";
    for (let i = 0; i < 3; i++) {
      result += letters.charAt(Math.floor(Math.random() * letters.length));
    }
    return result;
  };

  const handlePreview = () => {
    if (!voice) {
      toast.error("Please select a voice first");
      return;
    }
    navigate("/playground", {
      state: { voice, prompt: systemPrompt },
    });
  };

  // Inactivity message handlers
  const handleInactivityMessageChange = (index, field, value) => {
    setInactivityMessages((prev) =>
      prev.map((msg, i) =>
        i === index ? { ...msg, [field]: value } : msg
      )
    );
  };

  const addInactivityMessage = () => {
    setInactivityMessages((prev) => [
      ...prev,
      { duration: "", message: "" },
    ]);
  };

  const removeInactivityMessage = (index) => {
    if (inactivityMessages.length === 1) {
      toast.error("At least one inactivity message is required");
      return;
    }
    setInactivityMessages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!avatarName.trim() || !phoneNumber || !voice) {
      toast.error("Please fill all required fields");
      return;
    }

    // Validate inactivity messages
    for (const msg of inactivityMessages) {
      if (!msg.duration || !msg.message) {
        toast.error("Please fill all inactivity durations and messages");
        return;
      }
      if (isNaN(msg.duration) || Number(msg.duration) <= 0) {
        toast.error("All inactivity durations must be positive numbers");
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const payload = {
        avatarName: avatarPrefix ? `${avatarPrefix}-${avatarName}` : avatarName,
        avatarImage: typeof avatarImage === "string" ? avatarImage : null,
        language,
        gender,
        voice,
        phoneNumber,
        temperature: parseFloat(temperature),
        firstSpeaking,
        firstSpeakerSettings: {
          agent: {
            text: welcomeMessage || "Hi there! How can I help you?",
          },
        },
        welcomeMessage,
        maxDuration: maxDuration ? `${maxDuration}s` : "",
        timeExceedMessage,
        type: "outbound",
        transcript,
        callRecording,
        bookCalendar,
        whatsapp,
        callTransfer,
        whatsappNumber: whatsappNumber || "",
        callTransferNumber: callTransferNumber || "",
        calComApiKey: calcomToken,
        inactivityMessages: inactivityMessages.map((msg) => ({
          duration: `${msg.duration}s`,
          message: msg.message,
        })),
        ...(model && { model }),
        ...(systemPrompt && { systemPrompt }),
      };

      const response = await axios.put(`${API_BASE_URL}/update/${agentId}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 200) {
        toast.success("Agent updated successfully!");
        navigate("/agents-template");
      }
    } catch (error) {
      handleApiError(error, "Failed to update agent");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm("Are you sure you want to discard changes?")) {
      navigate("/agents-template");
    }
  };

  const modelOptions = [
    { label: "Ultimate", value: "Ultimate" },
    { label: "Advanced", value: "Advanced" },
    { label: "Elite", value: "Elite" },
    { label: "Max", value: "Max" },
  ];

  // Audio handling
  useEffect(() => {
    const handlePlay = (event) => {
      const audioElements = document.querySelectorAll("audio");
      audioElements.forEach((audio) => {
        if (audio !== event.target) {
          audio.pause();
          audio.currentTime = 0;
        }
      });
    };

    document.addEventListener("play", handlePlay, true);
    return () => document.removeEventListener("play", handlePlay, true);
  }, []);

  if (isLoading) {
    return (
      <div className={styles.pageContainer}>
        <LoaderSpiner />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.container}>
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <h2>Error Loading Agent</h2>
            <p>{error}</p>
            <button onClick={handleCancel} className={styles.submitBtn}>
              Back to Agents
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <div className={styles.container}>
        <div className={styles.headerWrap}>
          <h1 className={styles.header}>Edit Outbound Agent</h1>
          <button
            onClick={handleCancel}
            className={styles.cancelBtn}
            disabled={isSubmitting}
          >
            Cancel
          </button>
        </div>

        <form className={styles.formCard} onSubmit={handleUpdate}>
          <div className={styles.gridWrap}>
            {/* Basic Information Section */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Basic Information</h2>
              <div className={styles.fieldGroup}>
                <div className={styles.fieldWrap}>
                  <label className={styles.label}>
                    Avatar Name <span className={styles.requiredIndicator}>*</span>
                  </label>
                  <input
                    type="text"
                    className={styles.input}
                    placeholder="Enter avatar name"
                    value={avatarPrefix ? `${avatarPrefix}-${avatarName}` : avatarName}
                    onChange={handleAvatarNameChange}
                    disabled={isSubmitting}
                    required
                    aria-label="Avatar Name"
                  />
                </div>

                <div className={styles.fieldWrap}>
                  <label className={styles.label}>Avatar Image</label>
                  <div
                    className={styles.uploadWrap}
                    onClick={() => !isSubmitting && setShowAvatarPanel(true)}
                  >
                    {avatarImage ? (
                      <img
                        src={
                          typeof avatarImage === "string"
                            ? avatarImage.startsWith("http")
                              ? avatarImage
                              : `https://prod.toxboxai.com${avatarImage}`
                            : URL.createObjectURL(avatarImage)
                        }
                        alt="Avatar Preview"
                        className={styles.avatarPreview}
                        onError={(e) => {
                          e.target.src = "https://via.placeholder.com/150?text=No+Image";
                        }}
                      />
                    ) : (
                      <span className={styles.uploadIcon}>Upload Image</span>
                    )}
                  </div>
                </div>

                {showAvatarPanel && (
                  <div
                    className={styles.sidePanelOverlay}
                    onClick={() => setShowAvatarPanel(false)}
                  >
                    <div
                      className={styles.sidePanel}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <h3 className={styles.sidePanelTitle}>Choose an Avatar</h3>
                      <div className={styles.avatarGrid}>
                        {templateImages.map((img, index) => (
                          <img
                            key={index}
                            src={img}
                            alt={`avatar-${index}`}
                            className={styles.avatarOption}
                            onClick={() => handleAvatarSelect(img)}
                            onError={(e) => {
                              e.target.src = "https://via.placeholder.com/150?text=No+Image";
                            }}
                          />
                        ))}
                      </div>
                      <label className={styles.uploadButton}>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileUpload}
                          disabled={isSubmitting}
                        />
                        Upload Custom Image
                      </label>
                      <button
                        className={styles.cameraButton}
                        onClick={() => setShowCamera(true)}
                        disabled={isSubmitting}
                      >
                        Capture with Camera
                      </button>
                      {showCamera && (
                        <div className={styles.cameraOverlay}>
                          <div className={styles.cameraContainer}>
                            <Webcam
                              audio={false}
                              ref={webcamRef}
                              screenshotFormat="image/jpeg"
                              className={styles.cameraFeed}
                            />
                            <div className={styles.cameraButtons}>
                              <button
                                onClick={capturePhoto}
                                className={styles.captureButton}
                                disabled={isSubmitting}
                              >
                                Capture
                              </button>
                              <button
                                onClick={() => setShowCamera(false)}
                                className={styles.cancelButton}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                      <button
                        className={styles.cancelButton}
                        onClick={() => setShowAvatarPanel(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                <div className={styles.fieldWrap}>
                  <label className={styles.label}>Language</label>
                  <select
                    className={styles.input}
                    value={language}
                    onChange={(e) => {
                      setLanguage(e.target.value);
                      setCurrentPage(1);
                    }}
                    disabled={isSubmitting}
                    aria-label="Language"
                  >
                    <option value="">Select language</option>
                    {languages.map((lang) => (
                      <option key={lang.value} value={lang.value}>
                        {lang.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.fieldWrap}>
                  <label className={styles.label}>
                    Phone Number <span className={styles.requiredIndicator}>*</span>
                  </label>
                  <select
                    className={styles.input}
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    disabled={isLoadingNumbers || isSubmitting}
                    required
                    aria-label="Phone Number"
                  >
                    <option value="">
                      {isLoadingNumbers ? "Loading..." : "Select phone number"}
                    </option>
                    {phoneNumbers.map((phoneObj) => (
                      <option key={phoneObj._id} value={phoneObj.phoneNumber}>
                        {phoneObj.phoneNumber}
                      </option>
                    ))}
                  </select>
                  {numberError && <p className={styles.error}>{numberError}</p>}
                </div>

                <div className={styles.fieldWrap}>
                  <label className={styles.label}>Gender</label>
                  <select
                    className={styles.input}
                    value={gender}
                    onChange={(e) => {
                      setGender(e.target.value);
                      setCurrentPage(1);
                    }}
                    disabled={isSubmitting}
                    aria-label="Gender"
                  >
                    <option value="">All Genders</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Voice Selection Section */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>
                Voice Selection <span className={styles.requiredIndicator}>*</span>
              </h2>
              {voice && (
                <div className={styles.selectedVoiceInfo}>
                  <strong>Currently Selected:</strong> {voice}
                  <button
                    type="button"
                    className={styles.clearVoiceBtn}
                    onClick={() => setVoice("")}
                    disabled={isSubmitting}
                  >
                    Clear
                  </button>
                </div>
              )}
              <div className={styles.voiceSelectionWrapper}>
                <div className={styles.fieldWrap}>
                  <label className={styles.label}>Search Voices</label>
                  <input
                    type="text"
                    className={styles.input}
                    placeholder="Search by voice name..."
                    value={voiceSearch}
                    onChange={(e) => {
                      setVoiceSearch(e.target.value);
                      setCurrentPage(1);
                    }}
                    disabled={isSubmitting || isLoadingVoices}
                    aria-label="Search Voices"
                  />
                </div>
                {isLoadingVoices ? (
                  <LoaderSpiner />
                ) : voiceError ? (
                  <div className={styles.error}>
                    <p>{voiceError}</p>
                    <button
                      onClick={fetchSupportingData}
                      className={styles.retryBtn}
                      disabled={isSubmitting}
                    >
                      Retry
                    </button>
                  </div>
                ) : voices.length === 0 ? (
                  <p className={styles.noVoices}>No voices available</p>
                ) : (
                  <>
                    <table className={styles.voiceTable}>
                      <thead>
                        <tr>
                          <th>Display Name</th>
                          <th>Gender</th>
                          <th>Language</th>
                          <th>Preview</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentVoices.map((v) => (
                          <tr key={v._id}>
                            <td>{v.displayName}</td>
                            <td>{v.gender || "Unknown"}</td>
                            <td>{getVoiceLanguage(v.displayName) || "Unknown"}</td>
                            <td>
                              <audio
                                controls
                                className={styles.audioPreview}
                                preload="metadata"
                              >
                                <source
                                  src={`https://prod.toxboxai.com${v.previewUrl}`}
                                  type="audio/mpeg"
                                />
                                Your browser does not support audio.
                              </audio>
                            </td>
                            <td>
                              <button
                                type="button"
                                className={`${styles.selectButton} ${voice === v.displayName
                                  ? styles.selectedVoiceButton
                                  : ""
                                  }`}
                                onClick={() => handleVoiceSelect(v.displayName)}
                                disabled={isSubmitting}
                              >
                                {voice === v.displayName ? "✓ Selected" : "Select"}
                              </button>
                            </td>
                          </tr>
                        ))}
                        {currentVoices.length === 0 && (
                          <tr>
                            <td colSpan="5" className={styles.noVoices}>
                              No voices match the selected criteria
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>

                    {totalPages > 1 && (
                      <div className={styles.pagination}>
                        <button
                          type="button"
                          className={styles.paginationButton}
                          onClick={() => paginate(currentPage - 1)}
                          disabled={currentPage === 1 || isSubmitting}
                        >
                          Prev
                        </button>
                        <span>
                          {currentPage} of {totalPages}
                        </span>
                        <button
                          type="button"
                          className={styles.paginationButton}
                          onClick={() => paginate(currentPage + 1)}
                          disabled={currentPage === totalPages || isSubmitting}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Agent Configuration Section */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Agent Configuration</h2>
              <div className={styles.fieldGroup}>
                <div className={styles.fieldWrap}>
                  <label className={styles.label}>Model</label>
                  <select
                    className={styles.input}
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    disabled={isSubmitting}
                    aria-label="Model"
                  >
                    <option value="">Select model</option>
                    {modelOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.fieldWrap}>
                  <label className={styles.label}>System Prompt</label>
                  <select
                    className={styles.input}
                    value={systemPrompt}
                    onChange={(e) => setSystemPrompt(e.target.value)}
                    disabled={true}
                    aria-label="System Prompt"
                  >
                    <option value="">
                      {isLoadingPrompts ? "Loading..." : "Select system prompt"}
                    </option>
                    {prompts.map((p) => (
                      <option key={p._id} value={p._id}>
                        {p.promptName}
                      </option>
                    ))}
                  </select>
                  {promptError && <p className={styles.error}>{promptError}</p>}
                </div>

                <div className={styles.fieldWrap}>
                  <label className={styles.label}>
                    Temperature
                    <span className={styles.tooltipContainer}>
                      <GrFormView className={styles.viewicon} />
                      <span className={styles.tooltipText}>
                        Controls response randomness (0.1-1.0)
                      </span>
                    </span>
                  </label>
                  <div className={styles.sliderWrapper}>
                    <input
                      type="range"
                      min="0.1"
                      max="1.0"
                      step="0.1"
                      value={temperature}
                      onChange={(e) => setTemperature(Number(e.target.value))}
                      className={styles.slider}
                      disabled={isSubmitting}
                      aria-label="Temperature"
                    />
                    <span className={styles.sliderValue}>{temperature}</span>
                  </div>
                </div>

                <div className={styles.toggleCard}>
                  <div className={styles.toggleLabelWrap}>
                    <span className={styles.label}>First Speaking</span>
                    <label className={styles.switch}>
                      <input
                        type="checkbox"
                        checked={firstSpeaking}
                        onChange={(e) => setFirstSpeaking(e.target.checked)}
                        disabled={isSubmitting}
                        aria-label="First Speaking"
                      />
                      <span className={styles.sliderSwitch}></span>
                    </label>
                  </div>

                </div>

                {firstSpeaking && (
                  <div className={styles.fieldWrap}>
                    <label className={styles.label}>Welcome Message</label>
                    <textarea
                      className={styles.textarea}
                      rows={3}
                      placeholder="Enter welcome message for agent"
                      value={welcomeMessage}
                      onChange={(e) => setWelcomeMessage(e.target.value)}
                      disabled={isSubmitting}
                      aria-label="Welcome Message"
                    />
                  </div>
                )}

                <div className={styles.fieldWrap}>
                  <label className={styles.label}>Max Duration (seconds)</label>
                  <input
                    type="number"
                    className={styles.input}
                    placeholder="e.g., 300"
                    value={maxDuration}
                    onChange={(e) => setMaxDuration(e.target.value)}
                    disabled={isSubmitting}
                    aria-label="Max Duration"
                  />
                  <small className={styles.helperText}>
                    Will be sent as "300s" format
                  </small>
                </div>

                <div className={styles.fieldWrap}>
                  <label className={styles.label}>Time Exceed Message</label>
                  <input
                    type="text"
                    className={styles.input}
                    placeholder="Message when time limit exceeded"
                    value={timeExceedMessage}
                    onChange={(e) => setTimeExceedMessage(e.target.value)}
                    disabled={isSubmitting}
                    aria-label="Time Exceed Message"
                  />
                </div>


                {inactivityMessages.map((msg, index) => (
                  <div className={styles.fieldWrap}>
                    <label className={styles.label}>
                      Inactivity Messages <span className={styles.requiredIndicator}>*</span>
                      <span className={styles.tooltipContainer}>
                        <GrFormView className={styles.viewicon} />
                        <span className={styles.tooltipText}>
                          Inactivity messages are sent when the user is silent for the specified durations.
                        </span>
                      </span>
                    </label>
                    <div
                      key={index}
                      style={{ display: "flex", gap: "10px", marginBottom: "10px", alignItems: "center" }}
                    >
                      <input
                        type="number"
                        className={styles.input}
                        placeholder="Duration (seconds)"
                        value={msg.duration}
                        onChange={(e) => handleInactivityMessageChange(index, "duration", e.target.value)}
                        disabled={isSubmitting}
                        aria-label={`Inactivity Duration ${index + 1}`}
                        style={{ width: "30%" }}
                      />
                      <input
                        type="text"
                        className={styles.input}
                        placeholder="Message"
                        value={msg.message}
                        onChange={(e) => handleInactivityMessageChange(index, "message", e.target.value)}
                        disabled={isSubmitting}
                        aria-label={`Inactivity Message ${index + 1}`}
                        style={{ width: "50%" }}
                      />

                    </div>
                  </div>

                ))}
                <div className={styles.fieldWrap}>
                  <label className={styles.label}>
                    Inactivity Button <span className={styles.requiredIndicator}>*</span>
                    <span className={styles.tooltipContainer}>
                      <GrFormView className={styles.viewicon} />
                      <span className={styles.tooltipText}>
                        Inactivity messages are sent when the user is silent for the specified durations.
                      </span>
                    </span>
                  </label>
                  <button
                    type="button"
                    className={styles.submitBtn}
                    onClick={addInactivityMessage}
                    disabled={isSubmitting}
                  >
                    Add Inactivity Message
                  </button>
                </div>

                <div className={styles.toggleRow}>
                  <span className={styles.label}>Enable Transcript</span>
                  <label className={styles.switch}>
                    <input
                      type="checkbox"
                      checked={transcript}
                      onChange={(e) => setTranscript(e.target.checked)}
                      disabled={isSubmitting}
                      aria-label="Enable Transcript"
                    />
                    <span className={styles.sliderSwitch}></span>
                  </label>
                </div>

                <div className={styles.toggleRow}>
                  <span className={styles.label}>Enable Call Recording</span>
                  <label className={styles.switch}>
                    <input
                      type="checkbox"
                      checked={callRecording}
                      onChange={(e) => setCallRecording(e.target.checked)}
                      disabled={isSubmitting}
                      aria-label="Enable Call Recording"
                    />
                    <span className={styles.sliderSwitch}></span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className={styles.buttonRow}>
            <button
              type="submit"
              className={styles.submitBtn}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Updating..." : "Update Agent"}
            </button>
          </div>
        </form>

        {/* Cal.com Popup */}
        {showCalcomPopup && (
          <div
            className={styles.popupOverlay}
            onClick={() => setShowCalcomPopup(false)}
          >
            <div className={styles.popupContent} onClick={(e) => e.stopPropagation()}>
              <h3>Cal.com API Key</h3>
              <input
                type="text"
                className={styles.input}
                placeholder="Enter Cal.com API Key"
                value={calcomToken}
                onChange={(e) => setCalcomToken(e.target.value)}
                disabled={isSubmitting}
                aria-label="Cal.com API Key"
              />
              <div className={styles.popupButtons}>
                <button
                  className={styles.submitBtn}
                  onClick={() => setShowCalcomPopup(false)}
                  disabled={isSubmitting}
                >
                  Save
                </button>
                <button
                  className={styles.cancelBtn}
                  onClick={() => setShowCalcomPopup(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Call Transfer Popup */}
        {showCallTransferPopup && (
          <div
            className={styles.popupOverlay}
            onClick={() => setShowCallTransferPopup(false)}
          >
            <div className={styles.popupContent} onClick={(e) => e.stopPropagation()}>
              <h3>Call Transfer Number</h3>
              <input
                type="tel"
                className={styles.input}
                placeholder="+1234567890"
                value={callTransferNumber}
                onChange={(e) => setCallTransferNumber(e.target.value)}
                disabled={isSubmitting}
                aria-label="Call Transfer Number"
              />
              <div className={styles.popupButtons}>
                <button
                  className={styles.submitBtn}
                  onClick={() => setShowCallTransferPopup(false)}
                  disabled={isSubmitting}
                >
                  Save
                </button>
                <button
                  className={styles.cancelBtn}
                  onClick={() => setShowCallTransferPopup(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* WhatsApp Popup */}
        {showWhatsappPopup && (
          <div
            className={styles.popupOverlay}
            onClick={() => setShowWhatsappPopup(false)}
          >
            <div className={styles.popupContent} onClick={(e) => e.stopPropagation()}>
              <h3>WhatsApp Number</h3>
              <input
                type="tel"
                className={styles.input}
                placeholder="+1234567890"
                value={whatsappNumber}
                onChange={(e) => setWhatsappNumber(e.target.value)}
                disabled={isSubmitting}
                aria-label="WhatsApp Number"
              />
              <div className={styles.popupButtons}>
                <button
                  className={styles.submitBtn}
                  onClick={() => setShowWhatsappPopup(false)}
                  disabled={isSubmitting}
                >
                  Save
                </button>
                <button
                  className={styles.cancelBtn}
                  onClick={() => setShowWhatsappPopup(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        <ToastContainer />
      </div>
    </div>
  );
};

export default ElectionEditOutboundAgent;